import {useCallback, useEffect, useState} from "react";
import {
    ActionIcon,
    Button,
    Center,
    Divider,
    Group,
    Loader,
    Modal,
    NumberInput,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title
} from "@mantine/core";
import {IconGripVertical, IconPlus, IconTrash} from "@tabler/icons-react";
import {toast} from "react-toastify";
import api from '../../api';
import {DragDropContext, Draggable, Droppable, DropResult} from '@hello-pangea/dnd';

interface AdminRewardsTabProps {
    campaignPid: string;
}

interface Reward {
    pid: string;
    name: string;
    ordinalNumber: number;
}

export function AdminRewardsTab({ campaignPid }: AdminRewardsTabProps) {
    const [rewards, setRewards] = useState<Reward[]>([]);
    const [loading, setLoading] = useState(false);
    const [bulkInsertModalOpened, setBulkInsertModalOpened] = useState(false);

    // Bulk insert form state
    const [rewardName, setRewardName] = useState("");
    const [rewardCount, setRewardCount] = useState<number>(1);
    const [startPosition, setStartPosition] = useState<number>(1);
    const [inserting, setInserting] = useState(false);

    const fetchRewards = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/public/rewards`, {
                params: {
                    campaignPid: campaignPid,
                    page: 0,
                    size: 1000 // Get all rewards for display
                }
            });
            const rewardsData = response.data;

            if (rewardsData.content && Array.isArray(rewardsData.content)) {
                const sortedRewards = [...rewardsData.content].sort((a, b) => a.ordinalNumber - b.ordinalNumber);
                setRewards(sortedRewards);
            } else {
                setRewards([]);
            }
        } catch (error) {
            console.error("Error loading rewards:", error);
            toast.error("Neuspešno učitavanje nagrada.", { position: "top-center" });
        } finally {
            setLoading(false);
        }
    }, [campaignPid]);

    useEffect(() => {
        fetchRewards();
    }, [campaignPid, fetchRewards]);

    const handleBulkInsert = async () => {
        if (!rewardName.trim()) {
            toast.error("Molimo unesite naziv nagrade.", { position: "top-center" });
            return;
        }

        setInserting(true);
        try {
            await api.post(`/admin/${campaignPid}/rewards/bulk-insert`, {
                name: rewardName,
                count: rewardCount,
                startOrdinalPosition: startPosition
            });

            toast.success(`Uspešno dodato ${rewardCount} nagrada!`, { position: "top-center" });
            setBulkInsertModalOpened(false);
            setRewardName("");
            setRewardCount(1);
            setStartPosition(1);
            await fetchRewards();
        } catch (error: any) {
            const err = error?.response?.data?.message || "Greška prilikom dodavanja nagrada";
            toast.error(err, { position: "top-center" });
        } finally {
            setInserting(false);
        }
    };

    const handleDeleteReward = async (rewardPid: string, shouldRearrange: boolean) => {
        try {
            await api.delete(`/admin/rewards/${rewardPid}?rearrange=${shouldRearrange}`);
            toast.success("Nagrada uspešno obrisana!", { position: "top-center" });
            await fetchRewards();
        } catch (error: any) {
            const err = error?.response?.data?.message || "Greška prilikom brisanja nagrade";
            toast.error(err, { position: "top-center" });
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(rewards);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update ordinal numbers based on new positions
        const updatedItems = items.map((item, index) => ({
            ...item,
            ordinalNumber: index + 1
        }));

        // Optimistically update UI
        setRewards(updatedItems);

        // Send reorder request to backend
        try {
            const reorderData: Record<string, number> = {};
            updatedItems.forEach(item => {
                reorderData[item.pid] = item.ordinalNumber;
            });

            await api.post(`/admin/${campaignPid}/rewards/reorder`, {
                data: reorderData
            });

            toast.success("Redosled nagrada uspešno promenjen!", { position: "top-center" });
        } catch (error: any) {
            const err = error?.response?.data?.message || "Greška prilikom promene redosleda";
            toast.error(err, { position: "top-center" });
            // Revert on error
            await fetchRewards();
        }
    };

    const rewardTableRows = rewards.map((reward, index) => (
        <Draggable key={reward.pid} draggableId={reward.pid} index={index}>
            {(provided, snapshot) => (
                <Table.Tr
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    style={{
                        ...provided.draggableProps.style,
                        backgroundColor: snapshot.isDragging ? '#f0f9ff' : 'white',
                    }}
                >
                    <Table.Td>
                        <div {...provided.dragHandleProps}>
                            <IconGripVertical size={20} style={{ cursor: 'grab' }} />
                        </div>
                    </Table.Td>
                    <Table.Td>{reward.ordinalNumber}</Table.Td>
                    <Table.Td>{reward.name}</Table.Td>
                    <Table.Td>
                        <Group gap="xs">
                            <ActionIcon
                                color="red"
                                variant="light"
                                onClick={() => {
                                    if (window.confirm("Da li ste sigurni da želite da obrišete ovu nagradu i preuređujete ostale?")) {
                                        handleDeleteReward(reward.pid, true);
                                    }
                                }}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    </Table.Td>
                </Table.Tr>
            )}
        </Draggable>
    ));

    return (
        <Stack gap="md" className="mt-8">
            {/* Header with Bulk Insert Button */}
            <Paper p="md" withBorder>
                <Group justify="space-between">
                    <div>
                        <Title order={4}>Upravljanje nagradama</Title>
                        <Text size="sm" c="dimmed">Ukupno nagrada: <strong>{rewards.length}</strong></Text>
                    </div>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        color="teal"
                        onClick={() => setBulkInsertModalOpened(true)}
                    >
                        Dodaj nagrade
                    </Button>
                </Group>
            </Paper>

            {/* Instructions */}
            <Paper p="md" withBorder bg="blue.0">
                <Text size="sm" fw={500} mb="xs">Uputstvo:</Text>
                <ul className="text-sm space-y-1 ml-4">
                    <li>Prevucite nagrade za promenu redosleda</li>
                    <li>Koristite dugme "Dodaj nagrade" za masovno dodavanje</li>
                    <li>Brisanje nagrade automatski preuređuje redosled</li>
                </ul>
            </Paper>

            {/* Rewards Table */}
            {loading ? (
                <Center p="xl">
                    <Loader size="lg" color="teal" />
                </Center>
            ) : rewards.length === 0 ? (
                <Paper p="xl" withBorder>
                    <Center>
                        <Stack align="center">
                            <Text c="dimmed">Nema nagrada za prikaz</Text>
                            <Button
                                variant="light"
                                color="teal"
                                onClick={() => setBulkInsertModalOpened(true)}
                            >
                                Dodajte prvu nagradu
                            </Button>
                        </Stack>
                    </Center>
                </Paper>
            ) : (
                <ScrollArea>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: 50 }}></Table.Th>
                                    <Table.Th style={{ width: 100 }}>Red. br.</Table.Th>
                                    <Table.Th>Naziv nagrade</Table.Th>
                                    <Table.Th style={{ width: 100 }}>Akcije</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Droppable droppableId="rewards">
                                {(provided) => (
                                    <Table.Tbody {...provided.droppableProps} ref={provided.innerRef}>
                                        {rewardTableRows}
                                        {provided.placeholder}
                                    </Table.Tbody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                </ScrollArea>
            )}

            {/* Bulk Insert Modal */}
            <Modal
                opened={bulkInsertModalOpened}
                onClose={() => setBulkInsertModalOpened(false)}
                title="Masovno dodavanje nagrada"
                size="md"
            >
                <Stack gap="md">
                    <TextInput
                        label="Naziv nagrade"
                        placeholder="npr. Poklon vaučer 1000 RSD"
                        required
                        value={rewardName}
                        onChange={(e) => setRewardName(e.currentTarget.value)}
                    />
                    <NumberInput
                        label="Broj nagrada"
                        placeholder="Koliko nagrada dodati"
                        required
                        min={1}
                        max={1000}
                        value={rewardCount}
                        onChange={(val) => setRewardCount(val as number)}
                    />
                    <NumberInput
                        label="Početna pozicija"
                        placeholder="Od kog rednog broja početi"
                        required
                        min={1}
                        value={startPosition}
                        onChange={(val) => setStartPosition(val as number)}
                    />

                    <Divider />

                    <Text size="sm" c="dimmed">
                        Biće dodato <strong>{rewardCount}</strong> nagrada sa nazivom "<strong>{rewardName}</strong>"
                        počevši od pozicije <strong>{startPosition}</strong>.
                    </Text>

                    <Group justify="flex-end" mt="md">
                        <Button
                            variant="outline"
                            onClick={() => setBulkInsertModalOpened(false)}
                            disabled={inserting}
                        >
                            Otkaži
                        </Button>
                        <Button
                            color="teal"
                            onClick={handleBulkInsert}
                            loading={inserting}
                        >
                            Dodaj nagrade
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}
